import { backgroundOutputFile } from '@/lib/desktop-panel-helpers';
import type { DynamicToolPart } from './types';

/**
 * Map a terminal exit code onto the tool lifecycle state the header renders
 * with: still running, succeeded, or failed.
 */
export function terminalToolState(
  exitCode: number | null,
  signal: string | null = null,
): DynamicToolPart['state'] {
  if (exitCode === null) return signal === null ? 'input-available' : 'output-error';
  return exitCode === 0 ? 'output-available' : 'output-error';
}

export function terminalToolAction(exitCode: number | null, signal: string | null = null): string {
  if (exitCode !== null) return 'Ran';
  return signal === null ? 'Running' : 'Stopped';
}

export function terminalToolTitle(exitCode: number | null, signal: string | null = null): string {
  if (exitCode !== null) return `terminal · exit ${exitCode}`;
  return signal === null ? 'Terminal output' : `terminal · ${signal}`;
}

/**
 * The trailing status label shown inside a tool's embedded terminal, e.g.
 * `Running`, `Exit 0`, or `Exit 1 · SIGTERM`.
 */
export function terminalExitLabel(
  exitCode: number | null,
  signal: string | null,
  toolState?: DynamicToolPart['state'],
): string {
  if (exitCode === null) {
    if (signal !== null) return `Signal ${signal}`;
    if (toolState === 'output-error') return 'Failed';
    if (toolState === 'output-available') return 'Completed';
    return 'Running';
  }
  return `Exit ${exitCode}${signal === null ? '' : ` · ${signal}`}`;
}

/**
 * A terminal is still live while it has neither an exit code nor a terminating
 * signal — the same predicate `messageAgentTerminals` uses to flag `running`.
 */
export function terminalRunning(
  exitCode: number | null,
  signal: string | null,
  toolState?: DynamicToolPart['state'],
): boolean {
  return (
    exitCode === null &&
    signal === null &&
    toolState !== 'output-available' &&
    toolState !== 'output-error'
  );
}

/** A detached background task stays live after its launcher exits, unless it was signalled. */
export function backgroundTerminalRunning(
  exitCode: number | null,
  signal: string | null,
  hasOutputFile: boolean,
  backgroundCompleted = false,
): boolean {
  if (signal !== null || backgroundCompleted) return false;
  return terminalRunning(exitCode, signal) || hasOutputFile;
}

/**
 * A backgrounded command redirects its output to a file and keeps running, so
 * the agent's terminal output announces where the stream is being written. That
 * marker is how we tell a background terminal apart from an inline one.
 */
export function isBackgroundTerminal(output: string): boolean {
  return backgroundOutputFile(output) !== null;
}

export type BackgroundTerminalCompletion = {
  exitCode: number | null;
  signal: string | null;
};

/** Completion marker appended to a detached task's durable output by the native runtime. */
export function backgroundOutputCompletion(output: string): BackgroundTerminalCompletion | null {
  const normalized = output.trimEnd();
  if (normalized.endsWith('[killed]')) return { exitCode: null, signal: 'killed' };
  const match = normalized.match(/\[exited with code (-?\d+)\]$/i);
  if (match?.[1] === undefined) return null;
  return { exitCode: Number.parseInt(match[1], 10), signal: null };
}

/** Compact tail used by transcript cards without duplicating the full terminal pane. */
export function backgroundOutputTail(output: string, maxLines = 3): string {
  if (maxLines <= 0) return '';
  const normalized = output.replaceAll('\r\n', '\n').trimEnd();
  if (normalized.length === 0) return '';
  return normalized.split('\n').slice(-maxLines).join('\n');
}
